import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import {
  UPDATE_MERCHANT_INTEGRATION_SETTINGS,
  UPDATE_MERCHANT_LOYALTY_ENABLED
} from '../queries'
import { useIntegration } from 'hooks/useGlobalContext/useIntegration'

export interface SaveLoyaltySettingsVariables {
  enabled: boolean
  password?: string
}

// Both mutations return the full updated lightspeed-loyalty slice, so merging
// the result into Context keeps it in sync without a refetch round trip.
export const useLoyaltySettings = () => {
  const { merchantIntegrations, setMerchantIntegrations } = useIntegration()

  const [updateLoyaltySettings, { loading: isSavingSettings }] = useMutation(
    UPDATE_MERCHANT_INTEGRATION_SETTINGS
  )

  const [updateLoyaltyEnabled, { loading: isSavingEnabled }] = useMutation(
    UPDATE_MERCHANT_LOYALTY_ENABLED
  )

  const mergeUpdatedSettings = (
    data:
      | { updateMerchantIntegrationSettings?: Record<string, unknown> | null }
      | null
      | undefined
  ) => {
    if (!merchantIntegrations || !data?.updateMerchantIntegrationSettings) {
      return
    }
    setMerchantIntegrations({
      ...merchantIntegrations,
      ...data.updateMerchantIntegrationSettings
    })
  }

  const saveLoyaltySettings = useCallback(
    async (variables: SaveLoyaltySettingsVariables) => {
      // Without a password there is nothing to write, so use the mutation that
      // omits the field entirely rather than sending an empty one.
      const result = !variables.password?.trim()
        ? await updateLoyaltyEnabled({
            variables: { lightspeed_loyalty_enabled: variables.enabled }
          })
        : await updateLoyaltySettings({
            variables: {
              lightspeed_loyalty_enabled: variables.enabled,
              lightspeed_loyalty_auth_password: variables.password
            }
          })
      mergeUpdatedSettings(result.data)
      return result
    },
    [updateLoyaltySettings, updateLoyaltyEnabled, merchantIntegrations]
  )

  return {
    loyaltySettings: merchantIntegrations,
    isLoading: merchantIntegrations === null,
    isSaving: isSavingSettings || isSavingEnabled,
    saveLoyaltySettings
  }
}
